import {
  type OpenChangeReason,
  autoUpdate,
  flip,
  hide,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { useReducedMotion } from '@react-spring/web';
import { interactionResponse } from '@zillow/yield-callback';
import { type SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react';

import type { MenuRootPropsInterface } from '../types/menu-root.types';
import type { UseMenuReturnInterface } from '../types/use-menu.types';

export function useMenu(props: Partial<MenuRootPropsInterface>): UseMenuReturnInterface {
  const {
    defaultOpen,
    offset: offsetValue = 8,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    overflowPadding = 8,
    placement = 'bottom-start',
    shouldCloseOnViewportLeave = false,
    shouldAwaitInteractionResponse = true,
    useClickProps,
    useDismissProps,
    useRoleProps,
  } = props;

  const elementsRef = useRef<Array<HTMLElement | null>>([]);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  /**
   * Accessibility props and handlers to connect group heading with content if not wrapped in group
   */
  const [labelId, setLabelId] = useState<string | undefined>();

  /**
   * Open state management (controlled or uncontrolled)
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = 'open' in props;
  const open = isControlled ? controlledOpen : uncontrolledOpen || false;

  /**
   * Floating UI setup for modal state management
   */
  const { context, refs, middlewareData, floatingStyles } = useFloating({
    placement,

    open,

    onOpenChange: async (state, event, reason) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateOpenState(state, event, reason);
    },

    whileElementsMounted: autoUpdate,

    /**
     * Middleware plugins have to be in certain order.
     * See: https://floating-ui.com/docs/useFloating#middleware
     */
    middleware: [
      // Translates the floating element along the specified axes
      offset(offsetValue),

      // Changes the placement of the floating element to keep it in view
      flip({
        fallbackPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
        padding: overflowPadding,
      }),

      // A data provider that allows you to hide the floating element
      ...(shouldCloseOnViewportLeave ? [hide({ padding: overflowPadding })] : []),
    ],
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up click and dismiss event handlers, and ARIA attributes based on role
   */
  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    disabledIndices: [],
  });

  const click = useClick(context, {
    enabled: true,
    event: 'mousedown',
    ...useClickProps,
  });

  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) => !(event.target as HTMLElement).closest('[data-c11n-component="Menu"]'),
    ...useDismissProps,
  });

  const role = useRole(context, {
    role: 'menu',
    ...useRoleProps,
  });

  const { getFloatingProps, getReferenceProps, getItemProps } = useInteractions([
    listNav,
    click,
    dismiss,
    role,
  ]);

  /**
   * Motion helpers and styles
   */
  const reduceMotion = useReducedMotion();

  const { isMounted, styles: floatingTransitionStyles } = useTransitionStyles(context, {
    duration: reduceMotion ? 0 : 250,
    initial: {
      opacity: 0,
      transform: 'scale(0.975)',
    },
  });

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newValue - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newValue: boolean, event?: Event, reason?: OpenChangeReason) => {
      if (isControlled) {
        setControlledOpen?.(newValue, event, reason);
      } else {
        setUncontrolledOpen(newValue);
        setControlledOpen?.(newValue, event, reason);
      }
    },
    [isControlled, setControlledOpen],
  );

  /**
   * Handles programmatic close action
   *
   * @param event - Event object
   */
  const onClose = useCallback(
    (event: SyntheticEvent) => {
      updateOpenState(false, event as unknown as Event, 'click');
    },
    [updateOpenState],
  );

  return useMemo(
    () => ({
      // Internal focus management
      activeIndex,
      elementsRef,

      // Open/mounted state and close handler
      open,
      isMounted,
      onClose,

      // Trigger and reference refs
      refs,

      // Trigger and reference props
      getFloatingProps,
      getReferenceProps,
      getItemProps,

      // Styles
      floatingStyles,
      floatingTransitionStyles,

      // Accessibility props and handlers
      labelId,
      setLabelId,

      // Floating UI data
      context,
      middlewareData,
    }),
    [
      activeIndex,
      open,
      isMounted,
      onClose,
      refs,
      getFloatingProps,
      getReferenceProps,
      getItemProps,
      floatingStyles,
      floatingTransitionStyles,
      labelId,
      context,
      middlewareData,
    ],
  );
}
